'use client';

import { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { SEARCH_TYPE } from '@/constants/searchType';
import { buildSocietySearchHref } from '@/lib/publicSearchPaths';

/** Query params that drive the mating predictor's pedigree/EPA detail view. */
export const MATING_VIEW_PARAM = 'matingView';
export const MATING_SIRE_ID_PARAM = 'matingSireId';
export const MATING_DAM_ID_PARAM = 'matingDamId';

/** Query params that persist the mating predictor search form (for shareable links). */
export const MATING_SIRES_PARAM = 'matingSires';
export const MATING_DAMS_PARAM = 'matingDams';
export const MATING_CALVING_YEARS_PARAM = 'matingCalvingYears';
export const MATING_MAX_ANIMALS_PARAM = 'matingMaxAnimals';

/**
 * Builds hrefs for navigating into / out of the pedigree+EPA detail view while
 * preserving the rest of the current query (search idents, paging, etc.).
 *
 * URLs are built with {@link buildSocietySearchHref} so they always resolve to
 * the canonical mating path (`/{org}/animals/mating`) regardless of whether the
 * user arrived via that path or the query-based `?searchType=mating` form —
 * using the raw `usePathname()` here would drop the `/mating` segment in the
 * latter case. Rendering these as real links lets the Back / "View Pedigree"
 * controls be opened in a new tab (right-click / cmd-click).
 */
export function useMatingResultsUrls() {
  const params = useParams<{ orgName?: string }>();
  const orgSegment = params.orgName ?? '';
  const searchParams = useSearchParams();

  const buildPedigreeHref = useCallback(
    (sireId: number, damId: number) => {
      const next = new URLSearchParams(searchParams.toString());
      next.set(MATING_VIEW_PARAM, 'pedigree');
      next.set(MATING_SIRE_ID_PARAM, String(sireId));
      next.set(MATING_DAM_ID_PARAM, String(damId));
      return buildSocietySearchHref(orgSegment, SEARCH_TYPE.MATING, next);
    },
    [orgSegment, searchParams],
  );

  const searchViewHref = useMemo(() => {
    const next = new URLSearchParams(searchParams.toString());
    next.delete(MATING_VIEW_PARAM);
    next.delete(MATING_SIRE_ID_PARAM);
    next.delete(MATING_DAM_ID_PARAM);
    return buildSocietySearchHref(orgSegment, SEARCH_TYPE.MATING, next);
  }, [orgSegment, searchParams]);

  return { buildPedigreeHref, searchViewHref };
}
